import { CssBaseline, PaletteMode } from '@mui/material';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { useMemo, ReactNode } from 'react';

import breakpoints from './breakpoints';
import componentsOverrides from './overrides';
import shadows, { customShadows } from './shadows';
import shape from './shape';
import typography from './typography';
import useSettings from '../hooks/useSettings';
import useWhiteLabel from '../hooks/useWhiteLabel';

type ThemeConfigProps = {
  children: ReactNode;
};

const ThemeConfig: React.FC<ThemeConfigProps> = ({ children }) => {
  const { themeMode, themeDirection } = useSettings();
  const { palette } = useWhiteLabel();

  const isLight = themeMode === 'light';

  const theme = useMemo(() => {
    const themeOptions = {
      palette: isLight
        ? { ...palette.light, mode: 'light' as PaletteMode }
        : { ...palette.dark, mode: 'dark' as PaletteMode },
      shape,
      typography,
      breakpoints,
      direction: themeDirection,
      shadows: isLight ? shadows.light : shadows.dark,
      customShadows: isLight ? customShadows.light : customShadows.dark,
    };

    const baseTheme = createTheme(themeOptions);

    baseTheme.components = componentsOverrides(baseTheme);

    return baseTheme;
  }, [isLight, themeDirection, palette]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default ThemeConfig;
